import styled from "@emotion/styled";
import { AnimatePresence } from "framer-motion";
import { Suspense, useCallback, useEffect, useState } from "react";
import ecdh from "../lib/ecdh";
import { Route, Routes, useLocation } from "react-router-dom";
import { routes } from "../constants/routes";
import Loading from "./components/Spinner";
import Home from "./pages/Home";
import Confirm from "./pages/SimpleCert/Confirm";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { alertShowState, optionState, sessionProps, sessionState } from "../atom/common";
import Alert from "./components/Alert";
import { Request } from "./pages/SimpleCert/Request";
import { popupShowState } from "../atom/simpleCert";
import Popup from "./components/Popup";
import Splash from "./pages/Home/Splash";
import axios from "axios";

const Dim = styled.div`
    display: none;
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
    width: 100vw;
    min-width: 270px;
    height: 100svh;
    overflow: hidden;
    border: none;
    border-radius: 25px;
    background: #fff;

    /** iOS safe-area 설정 */
    @supports (env(safe-area-inset-top)) {
        padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
    }
`;


function App() {
    const [loading, setLoading] = useState(true);
    const isAlert = useRecoilValue(alertShowState);
    const isPopup = useRecoilValue(popupShowState)
    const setOptions = useSetRecoilState(optionState);
    const setSession = useSetRecoilState(sessionState);

    const init = useCallback((event: MessageEvent) => {
        let { func, param } = event.data;
        if (!func) return;

        if (func === 'exchange') {
            ecdh.init();
            ecdh.deriveKey(param.pub);
            host().postMessage({ code: "K0000", message: "성공", func: "exchange_callback", param: { pub: ecdh.getPub() } }, event.origin);
            return;
        }

        if (param) param = receive(param);
        if (!param.token) return;

        switch (func) {
            case 'prepare':
                setOptions(param);
                axios.post<sessionProps>("/kiap-service/api/v1/prepare", {}, { headers: { Authorization: `Bearer ${param.token}` } }
                )
                    .then(response => {
                        host().postMessage({ code: "K0000", message: "성공", func: "init_complete" }, "*");
                        setSession(response.data);
                        setLoading(false);
                    })
                    .catch(error => {
                        if (error.response.status === 401) {
                            host().postMessage({ code: "K0000", message: "성공", func: "refresh" }, "*");
                            return;
                        }
                        const { code, message } = error.response.data;
                        host().postMessage({ code, message }, "*");

                        if (window.opener) window.close();
                        else host().postMessage({ code: "K0000", message: "성공", func: "close" }, "*");
                    })
                break;
        }
    },
        [setOptions, setSession]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        host().postMessage({ code: "K0000", message: "성공", func: "init" }, "*");
        window.addEventListener('message', init);
        return () => {
            window.removeEventListener('message', init);
        };
    }, []);

    if (loading) return <Splash />;
    return (
        <>
            <Dim />
            <Container>
                {isAlert && <Alert />} {isPopup && <Popup />}
                <AnimatePresence>
                    <Suspense fallback={<Loading />}>
                        <Routes>
                            <Route path={routes.home} element={<Home key={routes.home} />} />
                            <Route path={routes.simpleCert.request} element={<Request key={routes.simpleCert.request} />} />
                            <Route path={routes.simpleCert.confirm} element={<Confirm key={routes.simpleCert.confirm} />} />
                        </Routes>
                    </Suspense>
                </AnimatePresence>
            </Container>
        </>
    );
}

export default App;

function receive(param: string) {
    try {
        const decrypted = ecdh.decrypt(param);
        return decrypted && JSON.parse(decrypted);
    } catch (e) {
        return {}
    }
}

function host() {
    return window.opener || window.parent;
}